import { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TranslationContext } from "../../Components/TranslationContext";
import { PayPalButtons } from "@paypal/react-paypal-js";
import * as constants from "../../utils/constants";
import { getToken, fetchDataFromAPI, useErrorHandler } from "../../utils/hooks";
import { Button } from "@mui/material";
import Message from "../../Components/Message";
import ErrorMessage from "../../Components/ErrorMessage";

function Renew() {
  // 1. Hook calls
  const navigate = useNavigate();
  const { errorMessage, handleAPIError } = useErrorHandler();
  const { translations } = useContext(TranslationContext);
  const [message, setMessage] = useState("");
  const [uidmessage, setUidmessage] = useState(0);
  const [selectedSubscription, setSelectedSubscription] = useState("SUB12");
  const [availableSubscriptions, setAvailableSubscriptions] = useState("");
  const [paypalOrderId, setPaypalOrderId] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [loading, setLoading] = useState(true);

  // 2. Other variable declarations
  const API_URL_CREATE_ORDER = `${constants.API_URL}subscription/createOrder`;
  const API_URL_AVAILABLE_ARTICLES = `${constants.API_URL}subscription/availableArticles`;
  const API_URL_CAPTURE_ORDER = `${constants.API_URL}subscription/captureOrder`;
  const auth = `Bearer ${getToken()}`;

  // 3. Memoization and Effects
  const requestOptions = useMemo(
    () => ({
      headers: {
        Authorization: auth,
      },
    }),
    [auth],
  );

  useEffect(() => {
    let timer;
    if (message === translations.payment_successful + " " + translations.account_updated) {
      timer = setTimeout(() => {
        navigate("/User");
      }, 10000); // 10 seconds
    }
    return () => clearTimeout(timer); // Cleanup on unmount or if message changes
  }, [message, translations.payment_successful, translations.account_updated, navigate]);

  useEffect(() => {
    const fetchApiData = async () => {
      try {
        const response = await fetchDataFromAPI(API_URL_AVAILABLE_ARTICLES, requestOptions, null, false);
        let subs = [];
        response.forEach((s) => {
          const [k, d, v] = s.split(":");
          subs.push({ key: k, duration: d, price: v });
        });
        setAvailableSubscriptions(subs);
      } catch (error) {
        handleAPIError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchApiData(); // read data
  }, [API_URL_AVAILABLE_ARTICLES, requestOptions, handleAPIError]);

  const updateMessage = (msg) => {
    setMessage(msg);
    setUidmessage(Math.random());
  };

  // 4. Function definitions
  function onSubscriptionChanged(subscriptionKey) {
    setSelectedSubscription(subscriptionKey);
    createPaypalOrder(subscriptionKey);
  }

  async function createPaypalOrder(productKey) {
    try {
      setCreatingOrder(true);
      setPaypalOrderId(null);

      const response = await fetch(API_URL_CREATE_ORDER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth,
        },
        body: JSON.stringify({
          product: productKey,
          userid: localStorage.getItem("userId"),
          username: localStorage.getItem("username"),
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.id) {
        console.log(response);
        console.log(data);
        throw new Error(data.message || translations.paypal_error);
      }

      setPaypalOrderId(data.id);
    } catch (error) {
      handleAPIError(error);
    } finally {
      setCreatingOrder(false);
    }
  }

  // 5. Return statement
  return (
    <>
      <h1>{translations.subscription_renew_action}</h1>
      <ErrorMessage message={errorMessage} />
      <Message message={message} timestamp={uidmessage} />
      <div className="mainPageContainer flexVertical">
        {loading ? (
          <p>{translations.loading}</p>
        ) : (
          <>
            <div className="flexGrid subscription-options-container">
              {availableSubscriptions.map((sub) => (
                <div
                  key={sub.key}
                  className={`flex flexVertical subscription-option ${
                    selectedSubscription === sub.key ? "subscription-selected" : ""
                  }`}
                >
                  <h2>{translations[`subscription_${sub.duration}_title`]}</h2>
                  <h3>
                    {sub.duration} {translations.months} - {sub.price} €
                  </h3>
                  <h2>
                    {(sub.price / sub.duration).toFixed(2)} € / {translations.month}
                  </h2>
                  <div className="flex flexVertical flexSpaceBetween">
                    <div>{translations[`subscription_${sub.duration}_desc`]}</div>
                    <div>
                      <Button onClick={() => onSubscriptionChanged(sub.key)} variant="outlined" color="primary">
                        {translations.btnChoose}
                      </Button>
                    </div>
                    <div>{translations.one_shot_payment}</div>
                  </div>
                </div>
              ))}
            </div>
            <br></br>
            <div className="flex flexHorizontalCenter">
              <div className="paymentContainer">
                {creatingOrder && <p>{translations.loading}</p>}
                {paypalOrderId && (
                  <PayPalButtons
                    forceReRender={[paypalOrderId]}
                    style={{
                      shape: "rect",
                      layout: "vertical",
                      color: "silver",
                      label: "paypal",
                    }}
                    createOrder={() => paypalOrderId}
                    onApprove={async (data) => {
                      try {
                        const response = await fetch(API_URL_CAPTURE_ORDER + "/" + data.orderID, {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: auth,
                          },
                        });

                        const orderData = await response.json();

                        if (!response.ok) {
                          throw new Error(orderData.message || translations.paypal_error);
                        }

                        updateMessage(translations.payment_successful + " " + translations.account_updated);
                      } catch (error) {
                        handleAPIError(error);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Renew;
